import React, { Suspense, useEffect, useState } from 'react';
import { Text } from '@react-three/drei';
import { ROOMS } from '../rooms';
import { useCityStore } from '../city/cityStore';
import { serverNow } from '../services/worldSocket';
import { cityTime } from '../city/cityTime';

// RoomHostSign — "a handwritten sign with a time and no explanation will show
// the next time he will be there" (Khari, 2026-10-31).
//
// A square of cardboard leaning against the front of the decks, a time on it in
// marker, two strips of tape. Nothing else: no name, no "back at". It shows
// only while the owner is NOT in and there IS a next time (the server's
// `rooms[<room>].next`: their next stay in, today's or one they have already
// said yes to for tomorrow — API RoomHosts.forClient). Not in, nothing booked:
// no sign at all. Where it stands is the room's (`hostSign` in rooms.js, room
// space at the room's fit, `k` like the booth's).
//
// The font is Permanent Marker (Apache 2.0, public/fonts), loaded only when a
// sign is drawn.

const FONT = `${import.meta.env.BASE_URL || '/'}fonts/PermanentMarker-Regular.ttf`;
const W = 0.24;    // ≈53cm at this world's scale (a person is ~0.75u tall)
const H = 0.17;
const INK = '#141210';

/** "9:40 PM" → "9:40pm": how a time is written by hand. */
const handTime = (ms) => cityTime(ms).replace(/\s*([AP])M$/i, (_, x) => `${x.toLowerCase()}m`);

export default function RoomHostSign({ roomId, k = 1 }) {
  const spec = ROOMS[roomId]?.hostSign || null;
  const own = useCityStore((s) => s.snap?.rooms?.[roomId] || null);
  // Re-read every 15 s: a stay starting or ending changes the sign without a new snapshot.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!spec || !own?.host) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 15000);
    return () => clearInterval(t);
  }, [spec, own?.host]);

  if (!spec || !own?.host) return null;
  const now = serverNow();
  const inNow = !!own.inside && own.until > now;
  const next = Number(own.next) > now ? Number(own.next) : null;
  if (inNow || !next) return null;

  const at = [spec.pos[0] * k, (spec.pos[1] || 0) * k, spec.pos[2] * k];
  return (
    <group position={at} rotation={[0, spec.rotY || 0, 0]}>
      {/* Pivot at the bottom edge, leaning back against the decks. */}
      <group rotation={[-(spec.tilt ?? 0.28), 0, 0]}>
        <mesh position={[0, H / 2, 0]} castShadow receiveShadow>
          <boxGeometry args={[W, H, 0.004]} />
          <meshStandardMaterial color="#b8915f" roughness={0.95} metalness={0} emissive="#4a3520" emissiveIntensity={0.35} />
        </mesh>
        {/* Tape at the top corners. */}
        {[-1, 1].map((sx) => (
          <mesh key={sx} position={[sx * (W / 2 - 0.02), H - 0.006, 0.0025]} rotation={[0, 0, sx * 0.5]}>
            <planeGeometry args={[0.05, 0.016]} />
            <meshStandardMaterial color="#e8e2c8" roughness={0.6} transparent opacity={0.75} emissive="#4a4636" emissiveIntensity={0.3} />
          </mesh>
        ))}
        <Suspense fallback={null}>
          <Text
            font={FONT}
            position={[0.004, H / 2 - 0.004, 0.0035]}
            rotation={[0, 0, -0.06]}
            fontSize={0.064}
            maxWidth={W - 0.03}
            anchorX="center"
            anchorY="middle"
            textAlign="center"
            color={INK}
          >
            {handTime(next)}
          </Text>
        </Suspense>
      </group>
    </group>
  );
}
